import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// material-ui
import ButtonBase from '@mui/material/ButtonBase';
import CardContent from '@mui/material/CardContent';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import Avatar from '@/components/@extended/Avatar';
import MainCard from '@/components/MainCard';
import Transitions from '@/components/@extended/Transitions';
import IconButton from '@/components/@extended/IconButton';

import { useAuthContext } from '@/providers';
import { avatarUrlOf } from '@/hooks/fetch-hooks/users-api';
import { IAppUser } from '@/hooks';

// assets
import LogoutOutlined from '@ant-design/icons/LogoutOutlined';
import ProfileTab from './ProfileTab';

// ==============================|| HEADER CONTENT - PROFILE ||============================== //

export default function Profile() {
	const navigate = useNavigate();

	const { user, signOut } = useAuthContext();
	// `user` conține datele complete din api/users/me, inclusiv rolul exact din BE.
	const me = user as unknown as IAppUser | undefined;
	const userName = user ? `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() : '';
	// afișare: preferăm eticheta localizată (label), fallback pe name
	const roleLabel = me?.roles?.[0]?.label || me?.roles?.[0]?.name || '';
	const initial = (userName || user?.email || '?').charAt(0).toUpperCase();
	// Avatarul din context se actualizează la salvarea profilului (setUser) —
	// header-ul reflectă instant poza/numele, la fel ca NavUser din sidebar.
	const userAvatar = avatarUrlOf(user?.avatar);

	const handleLogout = async () => {
		try {
			await signOut();
			navigate('/auth', { replace: true });
		} catch (err) {
			console.error(err);
		}
	};

	const anchorRef = useRef<any>(null);
	const [open, setOpen] = useState(false);
	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen);
	};

	const handleClose = (event: MouseEvent | TouchEvent) => {
		if (anchorRef.current && anchorRef.current.contains(event.target)) {
			return;
		}
		setOpen(false);
	};

	return (
		<Box sx={{ flexShrink: 0, ml: 'auto' }}>
			<Tooltip title="Profil" disableInteractive>
				<ButtonBase
					sx={(theme) => ({
						p: 0.25,
						borderRadius: 1,
						'&:focus-visible': {
							outline: `2px solid ${theme.vars.palette.secondary.dark}`,
							outlineOffset: 2
						}
					})}
					aria-label="open profile"
					ref={anchorRef}
					aria-controls={open ? 'profile-grow' : undefined}
					aria-haspopup="true"
					onClick={handleToggle}
				>
					<Avatar
						alt={userName}
						src={userAvatar}
						size="sm"
						sx={{ '&:hover': { outline: '1px solid', outlineColor: 'primary.main' } }}
					>
						{initial}
					</Avatar>
				</ButtonBase>
			</Tooltip>
			<Popper
				placement="bottom-end"
				open={open}
				anchorEl={anchorRef.current}
				role={undefined}
				transition
				disablePortal
				popperOptions={{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 9]
							}
						}
					]
				}}
			>
				{({ TransitionProps }) => (
					<Transitions type="grow" position="top-right" in={open} {...TransitionProps}>
						<Paper
							sx={(theme) => ({
								boxShadow: theme.vars.customShadows.z1,
								width: 290,
								minWidth: 240,
								maxWidth: { xs: 250, md: 290 }
							})}
						>
							<ClickAwayListener onClickAway={handleClose}>
								<MainCard elevation={0} border={false} content={false}>
									<CardContent sx={{ px: 2.5, pt: 3, pb: 2 }}>
										<Stack
											direction="row"
											sx={{ justifyContent: 'space-between', alignItems: 'center' }}
										>
											{user && (
												<Stack direction="row" sx={{ gap: 1.25, alignItems: 'center' }}>
													<Avatar alt={userName} src={userAvatar} sx={{ width: 32, height: 32 }}>
														{initial}
													</Avatar>
													<Stack>
														<Typography variant="h6">{userName || '—'}</Typography>
														<Typography variant="body2" sx={{ color: 'text.secondary' }}>
															{roleLabel}
														</Typography>
													</Stack>
												</Stack>
											)}
											<Tooltip title="Deconectare">
												<IconButton
													size="large"
													sx={{ color: 'text.primary' }}
													onClick={handleLogout}
												>
													<LogoutOutlined />
												</IconButton>
											</Tooltip>
										</Stack>
									</CardContent>
									<Divider />
									<Box sx={{ py: 1 }}>
										<ProfileTab handleLogout={handleLogout} />
									</Box>
								</MainCard>
							</ClickAwayListener>
						</Paper>
					</Transitions>
				)}
			</Popper>
		</Box>
	);
}
